import { Box, Chip, ClickAwayListener } from '@mui/material'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import InputCheckbox from 'component/Input/InputCheckbox'
import { ListFilterDoctors } from 'features/counter/appointment/head-appointment'
import AutocompleteSelect from 'new-components/select/autocomplete-select'

import { BranchInterface } from 'api/master/clinic.api'
import { colors } from 'constants/theme'

type BranchValue = (string | number)[]

type BranchOption = { id: string | number; name: string }

type Props = {
  branches: BranchInterface[]
  value: BranchValue
  onchange: (e: { target: { value: BranchValue } }) => void
  height?: number | string
  disabled?: boolean
}

export default function BranchFilterSearch({
  branches,
  value,
  onchange,
  height = 40,
  disabled,
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [showInput, setShowInput] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [uiCleared, setUiCleared] = useState(false)

  const valueIsAll = _.includes(value, 'ALL')
  const isAll = valueIsAll && !uiCleared

  useEffect(() => {
    if (!valueIsAll) setUiCleared(false)
  }, [valueIsAll])

  const allOption: BranchOption = { id: 'ALL', name: t('STATUS.ALL') }
  const options: BranchOption[] = [
    allOption,
    ..._.map(branches, (b) => ({ id: b.branchId, name: b.branchName })),
  ]

  const selectedOptions: BranchOption[] = uiCleared
    ? []
    : isAll
      ? [allOption]
      : _.compact(value.map((v) => options.find((o) => o.id === v)))

  const enterSearchMode = () => {
    setOpen(true)
    setShowInput(true)
  }

  const exitSearchMode = () => {
    setShowInput(false)
    setInputValue('')
  }

  const handleChange = (_e: any, newValue: any, _reason: any, details: any) => {
    const option: BranchOption | undefined = details?.option
    exitSearchMode()

    if (!option) {
      setUiCleared(false)
      onchange({ target: { value: ['ALL'] } })
      return
    }

    if (option.id === 'ALL') {
      if (isAll) {
        setUiCleared(true)
        return
      }
      setUiCleared(false)
      onchange({ target: { value: ['ALL'] } })
      return
    }

    setUiCleared(false)
    const allBranchIds = branches.map((b) => b.branchId)

    const nextIds: BranchValue = isAll
      ? allBranchIds.filter((id) => id !== option.id)
      : (newValue || [])
          .map((o: BranchOption) => o.id)
          .filter((id: any) => id !== 'ALL')

    const becameAll =
      allBranchIds.length > 0 &&
      nextIds.length === allBranchIds.length &&
      allBranchIds.every((id) => nextIds.includes(id))

    if (becameAll || nextIds.length === 0) {
      onchange({ target: { value: ['ALL'] } })
      return
    }

    onchange({ target: { value: nextIds } })
  }

  return (
    <ClickAwayListener
      onClickAway={() => {
        setOpen(false)
        exitSearchMode()
      }}
    >
      <Box onClick={enterSearchMode}>
        <AutocompleteSelect
          open={open}
          onOpen={enterSearchMode}
          onClose={(_e, reason) => {
            if (reason === 'escape' || reason === 'toggleInput') {
              setOpen(false)
              exitSearchMode()
            }
          }}
          inputValue={inputValue}
          onInputChange={(_e, val, reason) => {
            if (reason === 'input' || reason === 'clear') setInputValue(val)
          }}
          multiple
          labelId="branch-filter-search"
          options={options}
          value={selectedOptions}
          height={height}
          disabled={disabled}
          classesOption="style-select-doctors"
          getOptionLabel={(option: any) => option?.name || ''}
          isOptionEqualToValue={(opt: any, v: any) => opt?.id === v?.id}
          filterOptions={(option: any) => option?.name || ''}
          noOptionsText={t('REGISTER.FORM.NOT_FOUND')}
          disableClearable
          onchange={handleChange}
          sxAutoComplete={{
            '.MuiOutlinedInput-root': {
              py: '4px !important',
              minHeight: height,
              alignItems: 'center !important',
            },
            '.MuiAutocomplete-input': showInput
              ? { minWidth: '30px !important', flexGrow: 1 }
              : {
                  width: '0 !important',
                  minWidth: '0 !important',
                  padding: '0 !important',
                  flex: 'none !important',
                },
          }}
          renderOption={(props, option: BranchOption) => {
            const checked =
              option.id === 'ALL'
                ? isAll
                : isAll || _.includes(value, option.id)
            return (
              <ListFilterDoctors {...props} key={option.id} component="li">
                <InputCheckbox
                  label={option.name}
                  checked={checked}
                  className="pe-none"
                />
              </ListFilterDoctors>
            )
          }}
          renderTags={(values: BranchOption[], getTagProps) => {
            const prefix = `${t('DASHBOARD.FILTER.BRANCH')}:`

            if (showInput) {
              return (
                <Box
                  sx={{
                    pl: 1,
                    flexShrink: 0,
                    color: colors.textPrimary,
                  }}
                >
                  {prefix}
                </Box>
              )
            }

            if (isAll || values.length === 0) {
              return (
                <Box
                  sx={{
                    pl: 1,
                    flex: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: colors.textPrimary,
                  }}
                >
                  {`${prefix} ${t('STATUS.ALL')}`}
                </Box>
              )
            }

            const [first, ...rest] = values
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  overflow: 'hidden',
                  pl: 1,
                  gap: 0.5,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Box sx={{ flexShrink: 0, color: colors.textPrimary }}>
                  {prefix}
                </Box>
                <Chip
                  {...getTagProps({ index: 0 })}
                  key={first.id}
                  label={first.name}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: 13,
                    maxWidth: 160,
                    backgroundColor: colors.themeSecondColor06,
                    color: colors.themeSecondColor,
                    '.MuiChip-deleteIcon': {
                      fontSize: 14,
                      color: `${colors.themeSecondColor} !important`,
                    },
                  }}
                />
                {rest.length > 0 && (
                  <Box
                    sx={{
                      flexShrink: 0,
                      fontSize: 13,
                      color: colors.textExtraLightGray,
                    }}
                  >
                    +{rest.length}
                  </Box>
                )}
              </Box>
            )
          }}
        />
      </Box>
    </ClickAwayListener>
  )
}
